import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { FileText, Plus, Send, Check, X, Trash2 } from 'lucide-react';
import { EnterpriseToolbar, EnterpriseTableHead, StatusBadge, EmptyState, SelectCheckbox, RowActions } from '@/components/EnterpriseTable';

export default function PurchaseRequisitionsPage() {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [prDialog, setPRDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [prForm, setPRForm] = useState({
    department: '', priority: 'normal', required_date: '', notes: '',
    items: [{ item_name: '', quantity: 1, unit: 'nos', estimated_price: 0, specifications: '' }]
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/purchase-requisitions');
      setRequisitions(res.data);
    } catch (err) {
      toast.error('Failed to load requisitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePR = async () => {
    try {
      await api.post('/purchase-requisitions', prForm);
      toast.success('Purchase requisition created');
      setPRDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create PR');
    }
  };

  const handleSubmitPR = async (prId) => {
    try {
      await api.put(`/purchase-requisitions/${prId}/submit`);
      toast.success('PR submitted for approval');
      fetchData();
    } catch (err) {
      toast.error('Failed to submit PR');
    }
  };

  const handleApprovePR = async (prId) => {
    try {
      await api.put(`/purchase-requisitions/${prId}/approve`);
      toast.success('PR approved');
      fetchData();
    } catch (err) {
      toast.error('Failed to approve PR');
    }
  };

  const handleRejectPR = async (prId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await api.put(`/purchase-requisitions/${prId}/reject?rejection_reason=${encodeURIComponent(reason)}`);
      toast.success('PR rejected');
      fetchData();
    } catch (err) {
      toast.error('Failed to reject PR');
    }
  };

  const resetForm = () => {
    setSelectedPR(null);
    setPRForm({
      department: '', priority: 'normal', required_date: '', notes: '',
      items: [{ item_name: '', quantity: 1, unit: 'nos', estimated_price: 0, specifications: '' }]
    });
  };

  const addItem = () => {
    setPRForm({
      ...prForm,
      items: [...prForm.items, { item_name: '', quantity: 1, unit: 'nos', estimated_price: 0, specifications: '' }]
    });
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...prForm.items];
    newItems[idx][field] = value;
    setPRForm({ ...prForm, items: newItems });
  };

  const removeItem = (idx) => {
    if (prForm.items.length > 1) {
      setPRForm({ ...prForm, items: prForm.items.filter((_, i) => i !== idx) });
    }
  };

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => sum + (item.quantity * item.estimated_price), 0) || 0;
  };

  const filteredRequisitions = requisitions.filter(pr => 
    pr.pr_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pr.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRequisitions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRequisitions.map(pr => pr.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleViewSelected = () => {
    const pr = requisitions.find(p => p.id === selectedIds[0]);
    if (pr) { setSelectedPR(pr); setViewDialog(true); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="purchase-requisitions-page">
      <EnterpriseToolbar
        title="Purchase Requisitions"
        totalCount={filteredRequisitions.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchData}
        onAdd={() => { resetForm(); setPRDialog(true); }}
        onView={handleViewSelected}
        onPrint={() => window.print()}
        selectedCount={selectedIds.length}
        addLabel="New PR"
        customActions={[
          { label: 'Submit for Approval', icon: Send, onClick: () => selectedIds.length === 1 && handleSubmitPR(selectedIds[0]), disabled: selectedIds.length !== 1 },
          { label: 'Approve', icon: Check, onClick: () => selectedIds.length === 1 && handleApprovePR(selectedIds[0]), disabled: selectedIds.length !== 1 },
          { label: 'Reject', icon: X, onClick: () => selectedIds.length === 1 && handleRejectPR(selectedIds[0]), disabled: selectedIds.length !== 1 },
        ]}
      />

      <div className="flex-1 overflow-auto">
        <Card className="border-slate-200 shadow-sm rounded-none border-x-0">
          <CardContent className="p-0">
            <Table>
              <thead>
                <tr className="bg-slate-100 border-b">
                  <EnterpriseTableHead className="w-12">
                    <SelectCheckbox 
                      checked={selectedIds.length === filteredRequisitions.length && filteredRequisitions.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredRequisitions.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="w-16">S.No</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PR Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PR Date</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Department</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Items</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="text-right">Est. Amount</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Priority</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Status</EnterpriseTableHead>
                  <EnterpriseTableHead className="w-32">Actions</EnterpriseTableHead>
                </tr>
              </thead>
              <tbody>
                {filteredRequisitions.map((pr, index) => (
                  <TableRow key={pr.id} className={`hover:bg-slate-50 ${selectedIds.includes(pr.id) ? 'bg-blue-50' : ''}`}>
                    <TableCell className="py-1 px-1.5">
                      <SelectCheckbox checked={selectedIds.includes(pr.id)} onCheckedChange={() => toggleSelect(pr.id)} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 py-1 px-1.5">{index + 1}</TableCell>
                    <TableCell className="font-mono text-xs font-medium text-slate-800 py-1 px-1.5">{pr.pr_number}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">
                      {new Date(pr.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{pr.department || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{pr.items?.length || 0} items</TableCell>
                    <TableCell className="text-right font-mono text-xs font-semibold text-slate-800 py-1 px-1.5">
                      ₹{calculateTotal(pr.items).toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="py-1 px-1.5"><StatusBadge status={pr.priority} /></TableCell>
                    <TableCell className="py-1 px-1.5"><StatusBadge status={pr.status} /></TableCell>
                    <TableCell className="py-1 px-1.5">
                      <RowActions
                        onView={() => { setSelectedPR(pr); setViewDialog(true); }}
                        customActions={[
                          ...(pr.status === 'draft' ? [{ icon: Send, onClick: () => handleSubmitPR(pr.id), title: 'Submit', className: 'text-blue-500' }] : []),
                          ...(pr.status === 'pending_approval' ? [
                            { icon: Check, onClick: () => handleApprovePR(pr.id), title: 'Approve', className: 'text-emerald-500' },
                            { icon: X, onClick: () => handleRejectPR(pr.id), title: 'Reject', className: 'text-red-500' }
                          ] : [])
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            
            {filteredRequisitions.length === 0 && (
              <EmptyState
                icon={FileText}
                title="No Purchase Requisitions Found"
                description="Create your first purchase requisition to get started"
                action={
                  <Button onClick={() => { resetForm(); setPRDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-4 w-4 mr-2" />Create PR
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create PR Dialog */}
      <Dialog open={prDialog} onOpenChange={setPRDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">New Purchase Requisition</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Department</Label>
                <Input value={prForm.department} onChange={(e) => setPRForm({...prForm, department: e.target.value})} placeholder="Kitchen, Housekeeping..." className="bg-white" />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Priority</Label>
                <Select value={prForm.priority} onValueChange={(v) => setPRForm({...prForm, priority: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Required By</Label>
                <Input type="date" value={prForm.required_date} onChange={(e) => setPRForm({...prForm, required_date: e.target.value})} className="bg-white" />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Requested Items</Label>
                <Button variant="outline" size="sm" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add Item</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead>
                    <tr className="bg-slate-100">
                      <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                      <EnterpriseTableHead>Item Description</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-24">Qty</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-28">Unit</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32">Est. Price</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32 text-right">Amount</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-12"></EnterpriseTableHead>
                    </tr>
                  </thead>
                  <tbody>
                    {prForm.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                        <TableCell>
                          <Input placeholder="Item name" value={item.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} className="text-center" />
                        </TableCell>
                        <TableCell>
                          <Select value={item.unit} onValueChange={(v) => updateItem(idx, 'unit', v)}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nos">Nos</SelectItem>
                              <SelectItem value="kg">Kg</SelectItem>
                              <SelectItem value="l">Ltr</SelectItem>
                              <SelectItem value="pcs">Pcs</SelectItem>
                              <SelectItem value="box">Box</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input type="number" value={item.estimated_price} onChange={(e) => updateItem(idx, 'estimated_price', Number(e.target.value))} />
                        </TableCell>
                        <TableCell className="text-right font-mono">₹{(item.quantity * item.estimated_price).toFixed(2)}</TableCell>
                        <TableCell>
                          {prForm.items.length > 1 && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeItem(idx)}>
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs font-medium text-slate-500 uppercase">Justification / Notes</Label>
              <Textarea value={prForm.notes} onChange={(e) => setPRForm({...prForm, notes: e.target.value})} placeholder="Reason for this purchase request..." className="min-h-[80px]" />
            </div>

            <div className="flex justify-end">
              <div className="w-64 p-4 bg-slate-50 rounded-lg text-right">
                <span className="text-slate-500">Estimated Total:</span>
                <p className="text-xl font-bold text-emerald-600 font-mono">₹{calculateTotal(prForm.items).toFixed(2)}</p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPRDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePR} className="bg-emerald-600 hover:bg-emerald-700">Create Requisition</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View PR Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">PR: {selectedPR?.pr_number}</DialogTitle>
              <div className="flex gap-2">
                <StatusBadge status={selectedPR?.priority} />
                <StatusBadge status={selectedPR?.status} />
              </div>
            </div>
          </DialogHeader>
          {selectedPR && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
                <div><span className="text-slate-500">Department:</span><p className="font-medium">{selectedPR.department || '-'}</p></div>
                <div><span className="text-slate-500">Requested By:</span><p className="font-medium">{selectedPR.requested_by_name || '-'}</p></div>
                <div><span className="text-slate-500">Date:</span><p className="font-medium">{new Date(selectedPR.created_at).toLocaleDateString('en-IN')}</p></div>
                <div><span className="text-slate-500">Required By:</span><p className="font-medium">{selectedPR.required_date ? new Date(selectedPR.required_date).toLocaleDateString('en-IN') : '-'}</p></div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead><tr className="bg-slate-100">
                    <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                    <EnterpriseTableHead>Item</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-center">Qty</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Est. Price</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Amount</EnterpriseTableHead>
                  </tr></thead>
                  <tbody>
                    {selectedPR.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-center">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="text-right font-mono">₹{item.estimated_price}</TableCell>
                        <TableCell className="text-right font-mono font-medium">₹{(item.quantity * item.estimated_price).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>

              {selectedPR.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Notes:</p>
                  <p className="text-sm">{selectedPR.notes}</p>
                </div>
              )}

              <div className="text-right">
                <span className="text-slate-500 text-sm">Estimated Total:</span>
                <p className="text-2xl font-bold text-emerald-600 font-mono">₹{calculateTotal(selectedPR.items).toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
            {selectedPR?.status === 'draft' && (
              <Button onClick={() => { handleSubmitPR(selectedPR.id); setViewDialog(false); }} className="bg-blue-600 hover:bg-blue-700">
                <Send className="h-4 w-4 mr-2" />Submit for Approval
              </Button>
            )}
            {selectedPR?.status === 'pending_approval' && (
              <>
                <Button onClick={() => { handleRejectPR(selectedPR.id); setViewDialog(false); }} variant="outline" className="text-red-600 border-red-200">
                  <X className="h-4 w-4 mr-2" />Reject
                </Button>
                <Button onClick={() => { handleApprovePR(selectedPR.id); setViewDialog(false); }} className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="h-4 w-4 mr-2" />Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
